import {UniformCredential} from '../../types';
import React, {useEffect, useState} from 'react';
import {useFlowRouter} from '../../router/flow-router';
import {SSIInformationVerifyPageConfig} from '../../ecosystem/ecosystem-config';
import {useLocation} from 'react-router-dom';
import {useMediaQuery} from 'react-responsive';
import {Trans, useTranslation} from 'react-i18next';
import {Text} from '../../components/Text';
import SSIPrimaryButton from '../../components/SSIPrimaryButton';
import {convertPIDToUniformCredential} from '../../utils/mapper/PIDMapper';
import {NonMobile} from "../..";
import RenderClaims from "../../components/RenderClaims";
import '../../css/typography.css';
import RenderClaimsTable from "../../components/RenderClaimsTable";

type State = {
    data: object | undefined // raw credential
    credentials: UniformCredential[];
};

const SSIInformationVerifyPage: React.FC = () => {
    const flowRouter = useFlowRouter<SSIInformationVerifyPageConfig>();
    const location = useLocation();
    const isTabletOrMobile = useMediaQuery({query: '(max-width: 767px)'});

    const pageConfig: SSIInformationVerifyPageConfig = flowRouter.getPageConfig();
    const {t} = useTranslation();
    const [payload, setPayload] = useState<UniformCredential[] | null>(null);
    const [state, setState] = useState<State>(() => {
        const locationState = location.state as State | undefined
        return {
            data: locationState?.data,
            credentials: locationState?.credentials || []
        }
    })


    const credential = state?.data
    // Mock data for testing
    // const credential = getMockCredentialSdJwt();
    //const credential = getMockCredentialMdoc();
    const numberOfColumns = pageConfig.numberOfColumns ?? 1;

    useEffect(() => {
        const fetchData = async () => {
            if (state.data && 'vp_token' in state.data) {
                const vp_tokens = Array.isArray(state.data.vp_token) ? state.data.vp_token : [state.data.vp_token]
                const uniformCredentials = await convertPIDToUniformCredential(vp_tokens)
                setPayload(uniformCredentials)
                setState(prevState => ({
                    ...prevState,
                    credentials: uniformCredentials
                }))
            }
        }

        fetchData()
    }, [state.data])

    function determineWidth() {
        if (pageConfig.leftPaneWidth && pageConfig.leftPaneWidth.includes('%')) {
            return '100%'
        }
        return isTabletOrMobile ? '50%' : '40%'
    }

    if (!payload) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{
            display: 'flex',
            height: '100vh',
            background: '#E2E4FE',
            overflow: 'hidden',
        }}>
            <div style={{
                display: 'flex',
                alignContent: 'center',
                margin: '15px',
                flex: 1,
                backgroundColor: '#FBFBFB',
                borderRadius: '15px',
                borderTopLeftRadius: '15px',
                borderBottomLeftRadius: '15px',
                overflow: 'hidden',
            }}>
                <NonMobile>
                    <div id={"photo"} style={{
                        display: 'flex',
                        width: pageConfig.leftPaneWidth ?? 'auto',
                        height: pageConfig.leftPaneWidth ? '100%' : 'auto',
                        flexDirection: 'column',
                        alignItems: 'center',
                        ...((pageConfig.photoLeft) && {background: `url(${pageConfig.photoLeft}) 0% 0% / contain no-repeat`}),
                        ...(pageConfig.backgroundColor && {backgroundColor: pageConfig.backgroundColor}),
                        ...(pageConfig.logo && {justifyContent: pageConfig.logo.justifyContent ?? 'center'})
                    }}>
                        {pageConfig.logo &&
                            <img
                                src={pageConfig.logo.src}
                                alt={pageConfig.logo.alt}
                                width={pageConfig.logo.width}
                                height={pageConfig.logo.height}
                            />
                        }
                    </div>
                </NonMobile>
                <div style={{
                    display: 'flex',
                    flexGrow: 1,
                    width: determineWidth(),
                    alignItems: 'center',
                    flexDirection: 'column',
                    ...(isTabletOrMobile && {height: '100vh'}),
                    ...(isTabletOrMobile && {gap: 24}),
                    ...(!isTabletOrMobile && {justifyContent: 'center', backgroundColor: '#FFFFFF'}),
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '85%',
                        height: '95%'
                    }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Text
                                style={{
                                    whiteSpace: 'pre-line',
                                    flexGrow: 0,
                                    textAlign: 'center',
                                    marginBottom: 12
                                }}
                                title={t(pageConfig.topTitle ?? 'sharing_data_confirm_right_pane_title').split('\n')}
                                lines={t(`${pageConfig.topDescription ?? 'sharing_data_confirm_right_pane_paragraph'}`).split('\r\n')}
                            />
                        </div>
                        <div style={{
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginTop: '8px',
                            marginBottom: '8px',
                            paddingRight: '25px',
                            paddingLeft: '25px',
                        }}>
                            {payload.map((credential, index) => (
                                <div key={index} style={{marginTop: '20px', textAlign: 'left', width: '100%'}}>
                                    {credential.transformedClaims &&
                                        <RenderClaimsTable payload={credential.transformedClaims}/>}
                                </div>
                            ))}
                        </div>
                        <div>
                            <SSIPrimaryButton
                                caption={t('label_next')}
                                onClick={async () => await flowRouter.nextStep({payload: state?.credentials})}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function getMockCredentialMdoc() {
    return {
        docType: 'org.iso.18013.5.1.mDL',
        version: '1.0',
        issuer: 'DE-BMVI',
        credentialID: 'dXJuOmV4YW1wbGU6ZGUtYm12aToxMjM0NTY=',
        given_name: 'ERIKA',
        family_name: 'MUSTERMANN',
        family_name_birth: 'GABLER',
        birth_date: '1964-08-12',
        age_birth_year: 1964,
        age_in_years: 59,
        age_over_12: true,
        age_over_14: true,
        age_over_16: true,
        age_over_18: true,
        age_over_21: true,
        age_over_65: false,
        birth_place: 'BERLIN',
        resident_country: 'DE',
        resident_city: 'KÖLN',
        resident_postal_code: '51147',
        resident_street: 'HEIDESTRAẞE 17',
        nationality: 'DE',
        issuance_date: '...',
        expiry_date: '...',
        issuing_country: 'DE',
        issuing_authority: 'DE',
    };
}

function getMockCredentialSdJwt() {
    return {
        vct: 'urn:eu.europa.ec.eudi:pid:1',
        iss: 'https://demo.pid-issuer.bundesdruckerei.de/c',
        issuing_country: 'DE',
        issuing_authority: 'DE',
        given_name: 'ERIKA',
        family_name: 'MUSTERMANN',
        birth_family_name: 'GABLER',
        birthdate: '1964-08-12',
        age_birth_year: 1964,
        age_in_years: 59,
        age_equal_or_over: {
            '12': true,
            '14': true,
            '16': true,
            '18': true,
            '21': true,
            '65': false,
        },
        place_of_birth: {
            locality: 'BERLIN',
        },
        address: {
            locality: 'KÖLN',
            postal_code: '51147',
            street_address: 'HEIDESTRAẞE 17',
        },
        nationalities: ['DE'],
    };
}

export default SSIInformationVerifyPage;
